export default function DeRiskingSection() {
  const pillars = [
    {
      icon: 'ri-error-warning-line',
      title: 'Sole-Source Loss Recovery',
      desc: 'When a critical supplier disappears overnight, we move fast to identify, qualify, and validate replacement sources — minimizing disruption to your operations.',
    },
    {
      icon: 'ri-links-line',
      title: 'Single-Source Dependency Mitigation',
      desc: 'We map your supply chain vulnerabilities and build redundant, multi-source strategies that eliminate single points of failure before they become crises.',
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Per-Kilogram Cost Optimization',
      desc: 'Exorbitant pricing is often a symptom of limited market visibility. We benchmark, negotiate, and restructure your ingredient spend to deliver measurable savings.',
    },
    {
      icon: 'ri-lock-password-line',
      title: 'Proprietary Access & Exclusivity',
      desc: 'We help you secure exclusive custom ingredients that your competitors simply cannot access — turning supply chain strategy into a lasting competitive moat.',
    },
  ];

  return (
    <section className="w-full bg-white py-24 px-6 md:px-16 overflow-hidden">
      <div className="max-w-7xl mx-auto">

        {/* Top label + heading */}
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20 items-start mb-16">
          <div className="lg:w-2/5">
            <span className="text-emerald-600 text-xs font-bold uppercase tracking-widest">
              Strategic Services
            </span>
            <h2
              className="text-4xl md:text-5xl font-extrabold text-stone-800 mt-4 leading-tight"
              style={{ fontFamily: "'DM Serif Display', serif" }}
            >
              Strategic De-Risking &amp;
              <br />
              <span className="text-emerald-600">Custom Development</span>
            </h2>
            <div className="w-16 h-1 bg-emerald-600 mt-6 rounded-full" />
          </div>

          <div className="lg:w-3/5 flex flex-col justify-center">
            <p className="text-gray-600 text-base leading-relaxed mb-5">
              We provide <strong className="text-stone-800">Strategic De-Risking and Custom Development Services</strong> for high-value, mission-critical ingredients. Whether you are navigating the sudden loss of a sole-source supplier, mitigating the vulnerability of a single-source dependency, or seeking to optimize exorbitant per-kilogram pricing, we work to deliver viable, validated solutions.
            </p>
            <p className="text-gray-600 text-base leading-relaxed">
              By breaking away from the commoditized ingredients used by your competitors, we help you secure <strong className="text-stone-800">Proprietary Access</strong> and exclusive custom ingredients — transforming supply chain fragility into a distinct, long-term competitive advantage.
            </p>
          </div>
        </div>

        {/* Pillar cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-16">
          {pillars.map((p) => (
            <div
              key={p.title}
              className="bg-stone-50 border border-stone-100 rounded-lg p-6 hover:-translate-y-1 transition-transform duration-200"
            >
              <div className="w-11 h-11 flex items-center justify-center rounded-md bg-emerald-700 mb-5">
                <i className={`${p.icon} text-white text-xl`} />
              </div>
              <h3 className="text-stone-800 font-bold text-sm mb-2 leading-snug">{p.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>

        {/* Bottom highlight banner */}
        <div className="relative rounded-lg overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/f234f48aed1e51692de7be95e5b5ae22.jpg"
            alt="Strategic De-Risking background"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-stone-900/80 via-stone-900/60 to-emerald-900/70" />
          <div className="relative z-10 px-10 py-12 flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="max-w-2xl">
              <p
                className="text-white text-2xl md:text-3xl font-extrabold leading-snug mb-3"
                style={{ fontFamily: "'DM Serif Display', serif" }}
              >
                Transform Supply Chain Fragility Into Competitive Advantage
              </p>
              <p className="text-stone-300 text-sm leading-relaxed">
                Mission-critical ingredients demand mission-critical sourcing. Let us build the resilient, proprietary supply chain your business deserves.
              </p>
            </div>
            <a
              href="mailto:info@max-sourcing.com"
              className="shrink-0 bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm px-8 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Start a Conversation
            </a>
          </div>
        </div>

      </div>
    </section>
  );
}
